import React, { useEffect, useState, useCallback } from "react";
import { useSearchParams, useOutletContext } from "react-router-dom";
import { Download, Lock, Inbox } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import StatusPill from "@/components/StatusPill";
import { InlineLoader } from "@/components/Spinner";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { dateLabel } from "@/lib/status";
import { downloadReport } from "@/lib/download";

const SUMMARY = [
  { key: "free", label: "Free", color: "#A8A29E" },
  { key: "taken", label: "Taken", color: "#0284C7" },
  { key: "sold_multiple", label: "Sold Multiple", color: "#EA580C" },
  { key: "total_actions", label: "Sale Actions", color: "#1C1917" },
];

export default function DailyReport() {
  const { info } = useOutletContext() || {};
  const [params, setParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const date = params.get("date") || info?.today || "";

  const load = useCallback(() => {
    if (!date) return;
    setLoading(true);
    api.get(`/report?date=${date}`).then((r) => setData(r.data)).catch((e) => toast.error(apiError(e))).finally(() => setLoading(false));
  }, [date]);

  useEffect(() => { load(); }, [load]);

  const onDate = (v) => {
    const p = new URLSearchParams(params);
    p.set("date", v);
    setParams(p);
  };

  return (
    <div className="space-y-5 bb-fade-up">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-heading text-3xl font-extrabold tracking-tight">Daily Report</h1>
          <p className="text-sm text-stone-500">{dateLabel(date)}</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Input
            type="date"
            value={date}
            max={info?.today}
            onChange={(e) => onDate(e.target.value)}
            data-testid="report-date-input"
            className="h-10 w-[170px] rounded-xl"
          />
          <Button onClick={() => downloadReport(date)} data-testid="report-export-btn" className="gap-2 rounded-xl font-semibold">
            <Download className="h-4 w-4" /> Export to Excel
          </Button>
        </div>
      </div>

      {data?.read_only && (
        <div className="flex items-center gap-2 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm font-semibold text-amber-700" data-testid="report-readonly-banner">
          <Lock className="h-4 w-4" /> Read-only historical report — past days cannot be edited.
        </div>
      )}

      {loading && !data ? (
        <InlineLoader />
      ) : (
        <>
          <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
            {SUMMARY.map((s) => (
              <div key={s.key} className="rounded-2xl border border-stone-200 bg-white p-5 shadow-sm" data-testid={`report-summary-${s.key}`}>
                <p className="font-heading text-4xl font-black tracking-tighter" style={{ color: s.color }}>{data?.summary?.[s.key] ?? 0}</p>
                <p className="text-sm font-semibold text-stone-500">{s.label}</p>
              </div>
            ))}
          </div>

          <div className="overflow-hidden rounded-2xl border border-stone-200 bg-white">
            <div className="border-b border-stone-100 px-5 py-4">
              <h2 className="font-heading text-lg font-bold">All baldachins</h2>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full min-w-[760px] text-sm" data-testid="report-table">
                <thead>
                  <tr className="border-b border-stone-200 bg-stone-50 text-left text-xs uppercase tracking-wide text-stone-500">
                    <th className="px-4 py-3 font-semibold">Baldachin</th>
                    <th className="px-4 py-3 font-semibold">Final Status</th>
                    <th className="px-4 py-3 font-semibold">Sale Count</th>
                    <th className="px-4 py-3 font-semibold">Workers Involved</th>
                    <th className="px-4 py-3 font-semibold">First Sold</th>
                    <th className="px-4 py-3 font-semibold">Last Sold</th>
                  </tr>
                </thead>
                <tbody>
                  {data?.rows?.map((r) => (
                    <tr key={r.number} className="border-b border-stone-100 last:border-0 hover:bg-stone-50" data-testid={`report-row-${r.number}`}>
                      <td className="px-4 py-2.5 font-heading text-base font-bold">#{r.number}</td>
                      <td className="px-4 py-2.5"><StatusPill status={r.status} /></td>
                      <td className="px-4 py-2.5 font-bold">{r.sale_count}x</td>
                      <td className="px-4 py-2.5 text-stone-600">{r.workers_involved.length ? r.workers_involved.join(", ") : "—"}</td>
                      <td className="px-4 py-2.5 text-stone-500">{r.first_sold_time || "—"}</td>
                      <td className="px-4 py-2.5 text-stone-500">{r.last_sold_time || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
